import { RefObject, useCallback, useEffect } from "react";

interface UseFocusReturnOptions {
  containerRef: RefObject<HTMLElement>;
  triggerRef: RefObject<HTMLElement>;
  isActive: boolean;
  onEscape?: () => void;
}

export function useFocusReturn({
  containerRef,
  triggerRef,
  isActive,
  onEscape,
}: UseFocusReturnOptions): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === "Escape" && isActive) {
        event.preventDefault();
        onEscape?.();
        triggerRef.current?.focus();
      }
    },
    [isActive, onEscape, triggerRef],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isActive) return;

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, [containerRef, isActive, handleKeyDown]);
}

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "[tabindex]:not([tabindex=\"-1\"])",
].join(", ");

interface UseFocusTrapOptions {
  containerRef: RefObject<HTMLElement>;
  isActive: boolean;
}

export function useFocusTrap({
  containerRef,
  isActive,
}: UseFocusTrapOptions): void {
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key !== "Tab" || !isActive) return;

      const container = containerRef.current;
      if (!container) return;

      const focusable = Array.from(
        container.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR),
      );
      if (focusable.length === 0) return;

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    },
    [containerRef, isActive],
  );

  useEffect(() => {
    const container = containerRef.current;
    if (!container || !isActive) return;

    container.addEventListener("keydown", handleKeyDown);
    return () => container.removeEventListener("keydown", handleKeyDown);
  }, [containerRef, isActive, handleKeyDown]);
}
